import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';

export default function DashboardPane({ onClose }) {
    const { userEmail } = useAddin();
    const [activeTab, setActiveTab] = useState('queue'); // queue, log, links
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [scanState, setScanState] = useState('idle'); // idle | running | done | error

    const fetchData = async (endpoint) => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('ioliberum_token');
            const res = await fetch(`/api/dashboard/${endpoint}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch data');
            const json = await res.json();
            setData(json.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'queue') fetchData('filing-queue');
        else if (activeTab === 'log') fetchData('communication-log');
        else if (activeTab === 'links') fetchData('email-matter-links');
    }, [activeTab]);

    const handleFullScan = async () => {
        setScanState('running');
        try {
            const token = localStorage.getItem('ioliberum_token');
            const res = await fetch('/api/webhook/trigger-full-scan', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            setScanState('done');
            setTimeout(() => setScanState('idle'), 3000);
        } catch (err) {
            setScanState('error');
            setTimeout(() => setScanState('idle'), 4000);
        }
    };

    const handleDeleteLink = async (id) => {
        if (!confirm('Are you sure you want to delete this matter link?')) return;
        try {
            const token = localStorage.getItem('ioliberum_token');
            const res = await fetch(`/api/dashboard/email-matter-links/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to delete link');
            fetchData('email-matter-links');
        } catch (err) {
            alert('Failed to delete: ' + err.message);
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#f9f9f9', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px', backgroundColor: '#00204a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontFamily: 'Cabin' }}>Admin Dashboard</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={handleFullScan}
                        disabled={scanState === 'running'}
                        style={{
                            padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: scanState === 'running' ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold',
                            backgroundColor: scanState === 'done' ? '#2e7d32' : scanState === 'error' ? '#c62828' : '#1565c0',
                            color: 'white', opacity: scanState === 'running' ? 0.7 : 1,
                        }}
                    >
                        {scanState === 'running' ? 'Triggering…' : scanState === 'done' ? 'Scan Triggered' : scanState === 'error' ? 'Failed' : 'Full Scan'}
                    </button>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '24px' }}>&times;</button>
                </div>
            </div>

            <div style={{ display: 'flex', borderBottom: '1px solid #ccc', backgroundColor: 'white' }}>
                <button style={{ flex: 1, padding: '12px', border: 'none', background: activeTab === 'queue' ? '#f0f0f0' : 'white', cursor: 'pointer' }} onClick={() => setActiveTab('queue')}>Filing Queue</button>
                <button style={{ flex: 1, padding: '12px', border: 'none', background: activeTab === 'log' ? '#f0f0f0' : 'white', cursor: 'pointer' }} onClick={() => setActiveTab('log')}>Run Log</button>
                <button style={{ flex: 1, padding: '12px', border: 'none', background: activeTab === 'links' ? '#f0f0f0' : 'white', cursor: 'pointer' }} onClick={() => setActiveTab('links')}>Matter Links</button>
            </div>

            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
                {loading && <p>Loading...</p>}
                {error && <p style={{ color: 'red' }}>{error}</p>}
                
                {!loading && !error && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'white' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
                                {activeTab === 'queue' && <>
                                    <th>Subject</th><th>Matter</th><th>Status</th><th>Attempts</th>
                                </>}
                                {activeTab === 'log' && <>
                                    <th>Started</th><th>Status</th><th>Scanned</th><th>Enqueued</th><th>Filed</th><th>Failed</th>
                                </>}
                                {activeTab === 'links' && <>
                                    <th>Conversation</th><th>Matter</th><th>Client</th><th>Actions</th>
                                </>}
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(row => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #eee' }}>
                                    {activeTab === 'queue' && <>
                                        <td style={{ padding: '8px' }}>{row.subject}</td>
                                        <td style={{ padding: '8px' }}>{row.matter_description}</td>
                                        <td style={{ padding: '8px' }}>{row.status}</td>
                                        <td style={{ padding: '8px' }}>{row.attempts}</td>
                                    </>}
                                    {activeTab === 'log' && <>
                                        <td style={{ padding: '8px' }}>{new Date(row.started_at).toLocaleString()}</td>
                                        <td style={{ padding: '8px' }}>{row.status}</td>
                                        <td style={{ padding: '8px' }}>{row.scanned_count}</td>
                                        <td style={{ padding: '8px' }}>{row.enqueued_count}</td>
                                        <td style={{ padding: '8px' }}>{row.filed_count}</td>
                                        <td style={{ padding: '8px' }}>{row.failed_count}</td>
                                    </>}
                                    {activeTab === 'links' && <>
                                        <td style={{ padding: '8px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.conversation_id}>{row.conversation_id}</td>
                                        <td style={{ padding: '8px' }}>{row.matter_description}</td>
                                        <td style={{ padding: '8px' }}>{row.client_name}</td>
                                        <td style={{ padding: '8px' }}>
                                            <button onClick={() => handleDeleteLink(row.id)} style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}>Delete</button>
                                        </td>
                                    </>}
                                </tr>
                            ))}
                            {data.length === 0 && <tr><td colSpan="4" style={{ padding: '16px', textAlign: 'center' }}>No data found</td></tr>}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
